import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { LogOut, User, ChevronRight, Plus, X, Lock, Edit2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { SPORTS_CONFIG, ENABLED_SPORTS } from '../config/sports';

export default function Athletes() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [athletes, setAthletes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [newAthleteName, setNewAthleteName] = useState('');
  const [selectedSports, setSelectedSports] = useState([]);
  const [isCreating, setIsCreating] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [editingAthlete, setEditingAthlete] = useState(null);
  const [editName, setEditName] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [athletesRes, subRes] = await Promise.all([
        api.get('/athletes'),
        api.get('/subscription/status')
      ]);
      setAthletes(athletesRes.data);
      setSubscription(subRes.data);
      
      // Set default selected sport from subscription
      if (subRes.data.selected_sports?.length > 0) {
        setSelectedSports([subRes.data.selected_sports[0]]);
      } else {
        setSelectedSports(['basketball']);
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditAthlete = async () => {
    if (!editName.trim() || !editingAthlete) return;
    
    try {
      await api.put(`/athletes/${editingAthlete.id}`, { name: editName.trim() });
      toast.success('Athlete name updated!');
      setEditingAthlete(null);
      setEditName('');
      fetchData();
    } catch (error) {
      toast.error('Failed to update athlete');
    }
  };

  // Get allowed sports based on subscription
  const getAllowedSports = () => {
    if (!subscription?.is_subscribed) {
      // Free trial users can use all sports
      return ENABLED_SPORTS;
    }
    // Subscribed users can only use their selected sports
    // If no sports selected (legacy users), allow all sports
    const selectedSports = subscription?.selected_sports || [];
    return selectedSports.length > 0 ? selectedSports : ENABLED_SPORTS;
  };

  const allowedSports = getAllowedSports();

  const handleToggleSport = (sport) => {
    // Check if sport is allowed
    if (!allowedSports.includes(sport)) {
      toast.error(`Upgrade your plan to access ${SPORTS_CONFIG[sport]?.name}`);
      return;
    }
    
    setSelectedSports(prev => {
      if (prev.includes(sport)) {
        if (prev.length === 1) return prev;
        return prev.filter(s => s !== sport);
      }
      return [...prev, sport];
    });
  };

  const handleCreateAthlete = async () => {
    if (!newAthleteName.trim()) {
      toast.error('Please enter a name');
      return;
    }

    setIsCreating(true);
    try {
      const response = await api.post('/athletes', {
        name: newAthleteName.trim(),
        sports: selectedSports
      });
      setAthletes([...athletes, response.data]);
      setShowModal(false);
      setNewAthleteName('');
      setSelectedSports(['basketball']);
      navigate(`/athlete/${response.data.id}`);
    } catch (error) {
      toast.error('Failed to create athlete');
    } finally {
      setIsCreating(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getSportEmojis = (sports = ['basketball']) => {
    return sports
      .filter(s => SPORTS_CONFIG[s])
      .map(s => SPORTS_CONFIG[s].emoji)
      .join(' ');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#000] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#00E5E5] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#000] p-4 pb-24">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-2">
          <img 
            src="https://customer-assets.emergentagent.com/job_0f2dc054-d2bc-44be-ab56-5351417afe21/artifacts/a2cocwjb_3B8AECB1-0E02-4890-89FD-1DB815461399.png" 
            alt="In The Stands" 
            className="h-12 w-auto"
          />
        </div>
        <div className="flex gap-2">
          <button
            data-testid="account-btn"
            onClick={() => navigate('/account')}
            className="p-2 text-gray-400 hover:text-white transition-colors"
          >
            <User className="w-6 h-6" />
          </button>
          <button
            data-testid="logout-btn"
            onClick={handleLogout}
            className="p-2 text-gray-400 hover:text-white transition-colors"
          >
            <LogOut className="w-6 h-6" />
          </button>
        </div>
      </div>

      <h1 className="text-2xl font-bold text-white mb-6">My Athletes</h1>

      {/* Athletes List */}
      {athletes.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20">
          <img 
            src="https://customer-assets.emergentagent.com/job_0f2dc054-d2bc-44be-ab56-5351417afe21/artifacts/a2cocwjb_3B8AECB1-0E02-4890-89FD-1DB815461399.png" 
            alt="In The Stands" 
            className="w-48 h-auto mb-4 opacity-60"
          />
          <h2 className="text-xl font-bold text-white mb-2">No Athletes Yet</h2>
          <p className="text-gray-400 text-center">
            Create your first athlete to start tracking stats
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {athletes.map((athlete) => (
            <div
              key={athlete.id}
              className="w-full bg-[#1A1A1A] rounded-2xl p-4 flex items-center gap-4"
            >
              <button
                data-testid={`athlete-card-${athlete.id}`}
                onClick={() => navigate(`/athlete/${athlete.id}`)}
                className="flex-1 flex items-center gap-4 hover:opacity-80 transition-opacity"
              >
                <div className="w-14 h-14 rounded-full bg-[#00E5E5] flex items-center justify-center flex-shrink-0">
                  <User className="w-8 h-8 text-black" />
                </div>
                <div className="flex-1 text-left min-w-0">
                  <h3 className="text-lg font-semibold text-white truncate">{athlete.name}</h3>
                  <p className="text-lg">{getSportEmojis(athlete.sports)}</p>
                </div>
              </button>
              <button
                data-testid={`edit-athlete-${athlete.id}`}
                onClick={(e) => {
                  e.stopPropagation();
                  setEditingAthlete(athlete);
                  setEditName(athlete.name);
                }}
                className="p-2 text-gray-400 hover:text-[#00E5E5] transition-colors"
              >
                <Edit2 className="w-5 h-5" />
              </button>
              <ChevronRight className="w-6 h-6 text-gray-500" />
            </div>
          ))}
        </div>
      )}

      {/* Add Athlete Button */}
      <div className="fixed bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black to-transparent">
        <button
          data-testid="add-athlete-btn"
          onClick={() => setShowModal(true)}
          className="w-full bg-[#00E5E5] text-black font-bold py-4 rounded-xl flex items-center justify-center gap-2 hover:bg-[#5FFBFB] transition-colors"
        >
          <Plus className="w-5 h-5" />
          Add Athlete
        </button>
      </div>

      {/* Create Athlete Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-50">
          <div className="bg-[#1A1A1A] rounded-2xl p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-white">New Athlete</h2>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-white">
                <X className="w-6 h-6" />
              </button>
            </div>

            <input
              type="text"
              data-testid="athlete-name-input"
              placeholder="Athlete Name"
              value={newAthleteName}
              onChange={(e) => setNewAthleteName(e.target.value)}
              className="w-full bg-[#000] text-white rounded-xl py-4 px-4 mb-4 focus:outline-none focus:ring-2 focus:ring-[#00E5E5]"
              autoFocus
            />

            <p className="text-sm text-gray-400 mb-3 font-semibold">
              SELECT SPORTS
              {subscription?.is_subscribed && (
                <span className="text-xs text-[#00E5E5] ml-2">
                  ({allowedSports.length} available in your plan)
                </span>
              )}
            </p>
            <div className="grid grid-cols-3 gap-2 mb-6">
              {ENABLED_SPORTS.map((sport) => {
                const config = SPORTS_CONFIG[sport];
                const isSelected = selectedSports.includes(sport);
                const isAllowed = allowedSports.includes(sport);
                return (
                  <button
                    key={sport}
                    data-testid={`sport-select-${sport}`}
                    onClick={() => handleToggleSport(sport)}
                    disabled={!isAllowed}
                    className={`p-3 rounded-xl border-2 transition-all relative ${
                      isSelected
                        ? 'border-[#00E5E5] bg-[#00E5E5]/10'
                        : isAllowed
                          ? 'border-transparent bg-[#000]'
                          : 'border-transparent bg-[#000] opacity-40'
                    }`}
                  >
                    {!isAllowed && (
                      <Lock className="absolute top-1 right-1 w-3 h-3 text-gray-500" />
                    )}
                    <span className="text-2xl block">{config.emoji}</span>
                    <span className={`text-xs ${isSelected ? 'text-[#00E5E5]' : 'text-gray-400'}`}>
                      {config.name}
                    </span>
                  </button>
                );
              })}
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowModal(false);
                  setNewAthleteName('');
                  setSelectedSports(['basketball']);
                }}
                className="flex-1 bg-[#333] text-white font-semibold py-4 rounded-xl"
              >
                Cancel
              </button>
              <button
                data-testid="create-athlete-btn"
                onClick={handleCreateAthlete}
                disabled={isCreating}
                className="flex-1 bg-[#00E5E5] text-black font-bold py-4 rounded-xl disabled:opacity-50"
              >
                {isCreating ? 'Creating...' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Athlete Modal */}
      {editingAthlete && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-50">
          <div className="bg-[#1A1A1A] rounded-2xl p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-white">Edit Athlete</h2>
              <button 
                onClick={() => {
                  setEditingAthlete(null);
                  setEditName('');
                }}
                className="text-gray-400 hover:text-white"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <label className="text-sm text-gray-400 mb-2 block">Athlete Name</label>
            <input
              type="text"
              data-testid="edit-athlete-name-input"
              placeholder="Athlete Name"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              className="w-full bg-[#000] text-white rounded-xl py-4 px-4 mb-6 focus:outline-none focus:ring-2 focus:ring-[#00E5E5]"
              autoFocus
            />

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setEditingAthlete(null);
                  setEditName('');
                }}
                className="flex-1 bg-[#333] text-white font-semibold py-4 rounded-xl"
              >
                Cancel
              </button>
              <button
                data-testid="save-athlete-name-btn"
                onClick={handleEditAthlete}
                disabled={!editName.trim()}
                className="flex-1 bg-[#00E5E5] text-black font-bold py-4 rounded-xl disabled:opacity-50"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
